import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  IconButton,
  Input,
  Stack,
  Typography,
  Select,
  Option,
  Alert,
  Table,
  CircularProgress,
} from '@mui/joy';
import {
  Plus as Add,
  Trash as Delete,
  UserPlus as PersonAdd,
  Check,
} from '@phosphor-icons/react';
import { useMutation } from '@tanstack/react-query';
import { getApiErrorMessage } from '../../../lib/apiError';
import { useRegisterStepSave, type RegisterStepSave } from '../useRegisterStepSave';
import api from '../../../lib/api';

interface TeamMember {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  is_organization_admin: boolean;
  status: 'pending' | 'sending' | 'sent' | 'error';
  error?: string;
}

interface TeamInvitationsStepProps {
  registerSave: RegisterStepSave;
}

export const TeamInvitationsStep: React.FC<TeamInvitationsStepProps> = ({
  registerSave,
}) => {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [newMember, setNewMember] = useState({
    email: '',
    first_name: '',
    last_name: '',
    is_organization_admin: false,
  });
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successCount, setSuccessCount] = useState(0);

  const createUser = useMutation({
    mutationFn: async (userData: {
      email: string;
      first_name: string;
      last_name: string;
      username: string;
      is_organization_admin: boolean;
    }) => {
      const response = await api.post('/api/users/', userData);
      return response.data;
    },
  });

  const handleAddMember = () => {
    if (!newMember.email.trim()) return;

    // Check for duplicate
    if (members.some((m) => m.email === newMember.email)) {
      setError('This email has already been added.');
      return;
    }

    setMembers([
      ...members,
      {
        id: `new-${Date.now()}`,
        ...newMember,
        status: 'pending',
      },
    ]);

    setNewMember({
      email: '',
      first_name: '',
      last_name: '',
      is_organization_admin: false,
    });
    setError(null);
  };

  const handleRemoveMember = (id: string) => {
    setMembers(members.filter((m) => m.id !== id));
  };

  const handleSendInvites = async () => {
    if (members.length === 0) {
      return;
    }

    setIsSending(true);
    setError(null);
    let sent = 0;

    for (let i = 0; i < members.length; i++) {
      const member = members[i];
      if (member.status === 'sent') continue;

      setMembers((prev) =>
        prev.map((m) =>
          m.id === member.id ? { ...m, status: 'sending' } : m
        )
      );

      try {
        // Generate username from email
        const username = member.email.split('@')[0].replace(/[^a-zA-Z0-9]/g, '');

        await createUser.mutateAsync({
          email: member.email,
          first_name: member.first_name || member.email.split('@')[0],
          last_name: member.last_name || '',
          username: `${username}_${Date.now()}`,
          is_organization_admin: member.is_organization_admin,
        });

        setMembers((prev) =>
          prev.map((m) =>
            m.id === member.id ? { ...m, status: 'sent' } : m
          )
        );
        sent++;
      } catch (err: any) {
        // The API client attaches the parsed body as `responseData`; reading the
        // axios-shaped `response.data` always missed and showed a bare status.
        const errorMsg = getApiErrorMessage(err, 'Failed to send');
        setMembers((prev) =>
          prev.map((m) =>
            m.id === member.id ? { ...m, status: 'error', error: errorMsg } : m
          )
        );
      }
    }

    setSuccessCount(sent);
    setIsSending(false);

    // Surface a partial failure instead of advancing as if all invites landed.
    const failed = members.length - sent;
    if (failed > 0) {
      throw new Error(
        `${failed} of ${members.length} invitation${members.length > 1 ? 's' : ''} could not be sent. See the list below.`,
      );
    }
  };

  // Run by the wizard's "Save & Continue" — without this the invites the user
  // entered were never sent at all.
  useRegisterStepSave(registerSave, handleSendInvites);

  return (
    <Box>
      {error && (
        <Alert color="danger" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {successCount > 0 && (
        <Alert color="success" sx={{ mb: 2 }} startDecorator={<Check />}>
          Successfully sent {successCount} invitation{successCount > 1 ? 's' : ''}!
        </Alert>
      )}

      {/* Add member form */}
      <Card variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography level="title-sm" sx={{ mb: 2 }}>
          Add Team Member
        </Typography>
        <Stack spacing={2}>
          <Input
            placeholder="Email address *"
            type="email"
            value={newMember.email}
            onChange={(e) =>
              setNewMember({ ...newMember, email: e.target.value })
            }
            onKeyDown={(e) => e.key === 'Enter' && handleAddMember()}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Input
              placeholder="First name (optional)"
              value={newMember.first_name}
              onChange={(e) =>
                setNewMember({ ...newMember, first_name: e.target.value })
              }
              sx={{ flex: 1 }}
            />
            <Input
              placeholder="Last name (optional)"
              value={newMember.last_name}
              onChange={(e) =>
                setNewMember({ ...newMember, last_name: e.target.value })
              }
              sx={{ flex: 1 }}
            />
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center">
            <Select
              value={newMember.is_organization_admin ? 'admin' : 'member'}
              onChange={(_, value) =>
                setNewMember({
                  ...newMember,
                  is_organization_admin: value === 'admin',
                })
              }
              sx={{ minWidth: 150 }}
            >
              <Option value="member">Member</Option>
              <Option value="admin">Admin</Option>
            </Select>
            <Button
              variant="soft"
              startDecorator={<Add />}
              onClick={handleAddMember}
              disabled={!newMember.email.trim() || isSending}
            >
              Add to List
            </Button>
          </Stack>
        </Stack>
      </Card>

      {/* Pending invitations */}
      {members.length > 0 ? (
        <Box sx={{ mb: 3 }}>
          <Typography level="title-sm" sx={{ mb: 1.5 }}>
            Pending Invitations ({members.length})
          </Typography>
          <Card variant="outlined">
            <Table>
              <thead>
                <tr>
                  <th style={{ width: '40%' }}>Email</th>
                  <th>Name</th>
                  <th style={{ width: '100px' }}>Role</th>
                  <th style={{ width: '80px' }}>Status</th>
                  <th style={{ width: '50px' }}></th>
                </tr>
              </thead>
              <tbody>
                {members.map((member) => (
                  <tr key={member.id}>
                    <td>{member.email}</td>
                    <td>
                      {member.first_name || member.last_name
                        ? `${member.first_name} ${member.last_name}`.trim()
                        : '-'}
                    </td>
                    <td>
                      {member.is_organization_admin ? 'Admin' : 'Member'}
                    </td>
                    <td>
                      {member.status === 'sending' && (
                        <CircularProgress size="sm" />
                      )}
                      {member.status === 'sent' && (
                        <Check color="var(--joy-palette-success-500)" />
                      )}
                      {member.status === 'error' && (
                        <Typography level="body-xs" color="danger">
                          {member.error || 'Error'}
                        </Typography>
                      )}
                      {member.status === 'pending' && (
                        <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                          Pending
                        </Typography>
                      )}
                    </td>
                    <td>
                      <IconButton
                        variant="plain"
                        color="danger"
                        size="sm"
                        onClick={() => handleRemoveMember(member.id)}
                        disabled={member.status === 'sending' || member.status === 'sent'}
                      >
                        <Delete />
                      </IconButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        </Box>
      ) : (
        <Card
          variant="soft"
          sx={{
            p: 3,
            textAlign: 'center',
            backgroundColor: 'neutral.100',
            mb: 3,
          }}
        >
          <PersonAdd size={32} color="var(--joy-palette-neutral-500)" style={{ marginBottom: 8 }} />
          <Typography level="body-sm" sx={{ color: 'neutral.600' }}>
            No team members added yet. Add team members above or skip this step
            to invite them later from Settings.
          </Typography>
        </Card>
      )}

      <Typography level="body-xs" sx={{ color: 'neutral.500', textAlign: 'center' }}>
        Team members will receive an email invitation to set up their account.
        <br />
        Invitations expire after 24 hours. You can resend from Settings &rarr; Users.
      </Typography>

    </Box>
  );
};

export default TeamInvitationsStep;
